/**
 * Utility functions for listing file uploads.
 *
 * Files are stored in listing.attributes.publicData.files as an array of:
 *   { key: string, url: string, name: string, size: number, mimetype: string }
 */

/** Allowed file types for listing documents */
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
  'image/webp',
];

export const MAX_FILE_SIZE_MB = 20;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Check if a file is valid for upload.
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFile = file => {
  if (!file) return { valid: false, error: 'No file selected' };
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { valid: false, error: `File type ${file.type} is not allowed` };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: `File is too large (max ${MAX_FILE_SIZE_MB}MB)` };
  }
  return { valid: true };
};

/**
 * Format file size for display.
 * @param {number} bytes
 */
export const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Upload a file to the listing files endpoint.
 * @param {File} file
 * @param {string} listingId
 * @returns {Promise<{ key, url, name, size, mimetype }>}
 */
export const uploadListingFile = async (file, listingId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('listingId', listingId);

  const res = await fetch('/api/listing-files/upload', {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
};

/**
 * Delete a file from S3 via server.
 * @param {string} key
 */
export const deleteListingFile = async key => {
  const res = await fetch('/api/listing-files/delete', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  if (!res.ok) throw new Error('Delete failed');
};
